import { OrganizationCoordinatesValue } from "@clients/velys-infrastructure/api";

export interface Organization {
  id: string;
  name: string;
  addresses?: { [key: string]: string };
  location_names?: string[];
  phone_numbers?: { [key: string]: string };
  logos?: { [key: string]: string };
  procedures?: string[];
  websites?: { [key: string]: string };
  emails?: { [key: string]: string };
  coordinates?: { [key: string]: OrganizationCoordinatesValue };
  timezone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export enum ImageType {
  LOGO = "LOGO",
  ICON = "ICON",
}

export enum OrgFileAction {
  Upload = "Upload",
  Download = "Download",
}

export interface ListOrganizationsResponse {
  listOrganizations: {
    items: Organization[];
    nextToken?: string;
  };
}

export interface GetOrganizationResponse {
  getOrganization: Organization;
}

export interface CreateOrganizationInput {
  name: string;
  location_names: string[];
  addresses?: string;
  phone_numbers?: string;
  logos?: { [key: string]: string };
  procedures?: string[];
  websites?: string;
  emails?: string;
  coordinates?: { [key: string]: OrganizationCoordinatesValue };
  timezone?: string;
}

export interface CreateOrganizationResponse {
  createOrganization: Organization;
}

export interface UpdateOrganizationInput {
  name?: string;
  location_names?: string[];
  addresses?: string;
  phone_numbers?: string;
  logos?: { [key: string]: string };
  procedures?: string[];
  websites?: string;
  emails?: string;
  coordinates?: { [key: string]: OrganizationCoordinatesValue };
  timezone?: string;
}

export interface UpdateOrganizationResponse {
  updateOrganization: Organization;
}

export interface UpdateOrganizationLogosInput {
  logos: string; // AWSJSON — JSON.stringify the logos object; set a key to null to delete it
}

export interface UpdateOrganizationLogosResponse {
  updateOrganizationLogos: Organization;
}

export interface RequestOrgImageUrlInput {
  orgId: string;
  type: ImageType;
  action: OrgFileAction;
  filename?: string;
  contentType?: string;
}

export interface RequestOrgImageUrlResponse {
  requestOrgImageUrl: {
    url: string;
    contentType?: string;
    headers?: string;
  };
}

export interface OrgLogosEvent {
  id: string;
  logos: { [key: string]: string };
}

// ---- Organizations Domain Queries & Mutations ----

export const LIST_ORGANIZATIONS = `
  query ListOrganizations($query: String, $pagination: PaginationInput) {
    listOrganizations(query: $query, pagination: $pagination) {
      items {
        id
        name
        addresses
        location_names
        phone_numbers
        logos
        procedures
        websites
        emails
        coordinates
        timezone
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_ORGANIZATION = `
  query GetOrganization($id: ID!) {
    getOrganization(id: $id) {
      id
      name
      addresses
      location_names
      phone_numbers
      logos
      procedures
      websites
      emails
      coordinates
      timezone
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_ORGANIZATION = `
  mutation CreateOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      id
      name
      addresses
      location_names
      phone_numbers
      logos
      procedures
      websites
      emails
      coordinates
      timezone
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_ORGANIZATION = `
  mutation UpdateOrganization($id: ID!, $input: UpdateOrganizationInput!) {
    updateOrganization(id: $id, input: $input) {
      id
      name
      addresses
      location_names
      phone_numbers
      logos
      procedures
      websites
      emails
      coordinates
      timezone
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_ORGANIZATION_LOGOS = `
  mutation UpdateOrganizationLogos($id: ID!, $input: UpdateOrganizationLogosInput!) {
    updateOrganizationLogos(id: $id, input: $input) {
      id
      logos
    }
  }
`;

export const REQUEST_ORG_IMAGE_URL = `
  mutation RequestOrgImageUrl($input: RequestOrgImageUrlInput!) {
    requestOrgImageUrl(input: $input) {
      url
      contentType
      headers
    }
  }
`;

// ---- Organizations Domain Subscriptions ----

export const ON_CREATE_ORGANIZATION = `
  subscription OnCreateOrganization {
    onCreateOrganization {
      id
      name
      addresses
      location_names
      phone_numbers
      logos
      procedures
      websites
      emails
      coordinates
      timezone
      createdAt
      updatedAt
    }
  }
`;

export const ON_UPDATE_ORGANIZATION = `
  subscription OnUpdateOrganization($id: ID!) {
    onUpdateOrganization(id: $id) {
      id
      name
      addresses
      location_names
      phone_numbers
      logos
      procedures
      websites
      emails
      coordinates
      timezone
      createdAt
      updatedAt
    }
  }
`;

export const ON_ORG_LOGOS_UPDATED = `
  subscription OnOrgLogosUpdated($id: ID!) {
    onOrgLogosUpdated(id: $id) {
      id
      logos
    }
  }
`;
